/**
 * Soft line breaks for canvas contenteditable (Shift+Enter).
 * Prefer `insertLineBreak` so browsers insert `<br>`; readers use `innerText` → `\n`.
 */

export function contentNeedsPreWrap(
	content: string | null | undefined
): boolean {
	return typeof content === "string" && content.includes("\n");
}

/** Preserve author newlines when not using HTML markup (`white-space: pre-wrap`). */
export function styleForPreservedLineBreaks(
	content: string | null | undefined
): { whiteSpace: "pre-wrap" } | undefined {
	return contentNeedsPreWrap(content) ? { whiteSpace: "pre-wrap" } : undefined;
}

/**
 * Insert a soft line break at the caret. Call after `preventDefault` on Shift+Enter.
 * Returns true when a break was inserted.
 */
export function insertContentEditableLineBreak(host: HTMLElement): boolean {
	if (typeof document === "undefined") {
		return false;
	}
	host.focus();
	try {
		if (document.execCommand("insertLineBreak")) {
			return true;
		}
	} catch {
		/* fall through */
	}
	const sel = window.getSelection();
	if (!(sel && sel.rangeCount > 0)) {
		return false;
	}
	const range = sel.getRangeAt(0);
	if (
		!(
			host === range.commonAncestorContainer ||
			host.contains(range.commonAncestorContainer)
		)
	) {
		return false;
	}
	range.deleteContents();
	const br = document.createElement("br");
	range.insertNode(br);
	// Trailing BR alone can be ignored by caret placement — keep a text node after.
	const after = document.createTextNode("");
	br.parentNode?.insertBefore(after, br.nextSibling);
	range.setStart(after, 0);
	range.collapse(true);
	sel.removeAllRanges();
	sel.addRange(range);
	return true;
}
